import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/authService';

export const useAdminTermsPrivacy = (initialFilters = {}) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    document_type: '',
    is_active: '',
    ...initialFilters
  });

  const documentTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'terms', label: 'Terms of Service' },
    { value: 'privacy', label: 'Privacy Policy' },
    { value: 'cookie', label: 'Cookie Policy' },
    { value: 'refund', label: 'Refund Policy' },
    { value: 'cancellation', label: 'Cancellation Policy' }
  ];

  const activeStatusOptions = [
    { value: '', label: 'All Documents' },
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' }
  ];

  const cleanFilters = useMemo(() => {
    return Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    );
  }, [filters]);

  const queryString = new URLSearchParams(cleanFilters).toString();

  const { data: documentsResponse, isLoading, error } = useQuery({
    queryKey: ['adminTermsPrivacy', cleanFilters],
    queryFn: async () => {
      const response = await api.get(`/admin/terms-privacy${queryString ? `?${queryString}` : ''}`);
      return response.data;
    }
  });

  // Fetch statistics
  const { data: statsResponse, isLoading: isLoadingStats } = useQuery({
    queryKey: ['adminTermsPrivacyStats'],
    queryFn: async () => {
      const response = await api.get('/admin/terms-privacy/statistics');
      return response.data;
    }
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/admin/terms-privacy', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminTermsPrivacy']);
      queryClient.invalidateQueries(['adminTermsPrivacyStats']);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/admin/terms-privacy/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminTermsPrivacy']);
      queryClient.invalidateQueries(['adminTermsPrivacyStats']);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/admin/terms-privacy/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminTermsPrivacy']);
      queryClient.invalidateQueries(['adminTermsPrivacyStats']);
    }
  });

  const documents = useMemo(() => {
    const data = documentsResponse?.data?.data || documentsResponse?.data;
    if (data && Array.isArray(data)) {
      return data;
    }
    if (data && data.data && Array.isArray(data.data)) {
      return data.data;
    }
    return [];
  }, [documentsResponse]);

  const statistics = useMemo(() => {
    return statsResponse?.data || {};
  }, [statsResponse]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSearch = (data, searchTerm) => {
    if (!searchTerm) return data;
    
    return data.filter(doc => 
      doc.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.document_type?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  return {
    documents,
    isLoading: isLoading || isLoadingStats,
    error,
    filters,
    documentTypeOptions,
    activeStatusOptions,
    handleFilterChange,
    handleSearch,
    statistics,
    createDocument: createMutation.mutate,
    isCreating: createMutation.isPending,
    updateDocument: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
    deleteDocument: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending
  };
};


